/* eslint-disable max-len */
import {DiceSettingEnum} from '../../../../../shared/GameEnums'
import {GameModeDataController} from '@colonist/shared-types'
import {MapDataController} from '../../../../../shared/map_data/MapDataController'
import {RoomState} from '../../../../../shared/RoomState'
import {BotSpeedSettings, GameSpeedSettings, LobbyImage} from '@colonist/types'
import {clientI18n} from '../../../../shared/scripts/ClientI18n'
import {SocketLobbySend} from '../../../../shared/scripts/socket/SocketLobbySend'
import {createDivIn, createHeaderIn, createImgIn, createLinkIn} from '../../../../shared/scripts/ui_utils/DomUtils'
import {UIMRoomEditableCard} from './UIMRoomEditableCardView'
import {UIMRoomToggableButton} from './UIMRoomToggableButton'
import {TranslationObject, getNestedTranslation} from '@colonist/i18n'
import {Sound} from '../../../../shared/scripts/Sound'
import {UIMRoomSettingsRulesInformationViewPopup} from './UIMRoomSettingsRulesInformationViewPopup'
import {EnumUtils} from '@colonist/utils'
import {RoomSettingConstants} from '@colonist/game-base-types'
import {notificationController, socket} from '../../../../shared/scripts/EntryShared'
import {UIRoomSliderController} from '../../../../shared/scripts/ui_html/room/UIRoomSliderController'
import {StorageWrapper} from '../../../../shared/scripts/StorageWrapper'
import {ClientUserState} from '../../../../shared/scripts/ClientUserState'
import {UITopNotificationType} from '../../../../shared/scripts/notifications/UINotificationController'

enum ToggleButtons {
    PrivateRoom,
    HideBankCards,
    FriendlyRobber,
    BalancedDice,
    NoTroll,
}

enum SliderButtons {
    MaxPlayers,
    VictoryPoints,
    CardDiscardLimit,
    GameSpeed,
    BotSpeed,
}
interface SettingsButton {
    id: ToggleButtons
    title: TranslationObject
    icon: LobbyImage
    isActive: boolean
    onClick: (active: boolean) => void
}

interface SliderButton {
    id: SliderButtons
    title: TranslationObject
    value: number
    min: number
    max: number
    step: number
    onChange: (value: number) => void
    options?: string[]
}

export class UIMRoomSettingsController {
    private readonly toggleButtons: {button: UIMRoomToggableButton, id: ToggleButtons} [] = []
    private readonly sliderButtons: {button: UIRoomSliderController, id: SliderButtons} [] = []

    private readonly gamemodeCard: UIMRoomEditableCard
    private readonly mapCard: UIMRoomEditableCard

    private readonly rulesHeader: HTMLHeadingElement
    private readonly rulesInformationButton: HTMLLinkElement

    private shouldSyncPrivateRoomStateToServer: boolean = false
    private unSyncedPrivateRoomState: boolean = false

    private readonly DEFAULT_SLIDER_STEP = 1
    private readonly ZERO_INDEX_OFFSET = 1

    private roomState: RoomState
    private isHighKarmaGameActive: boolean = false

    constructor(container: HTMLDivElement, roomState: RoomState, setActiveTab: (tabIdx: number) => void, shouldSyncPrivateRoomStateToServer: boolean = false) {
        this.shouldSyncPrivateRoomStateToServer = shouldSyncPrivateRoomStateToServer
        const isViewOnly = !roomState.isHost(socket.controller.session.id)
        const gameModeData = GameModeDataController.getModeDataForMode(roomState.gameSetting.modeSetting)
        if(gameModeData != undefined) {
            const modeName = getNestedTranslation({key: 'strings:game.settings.gameInfoTab.gameMode.value'}, {modeName: gameModeData.name})
            this.gamemodeCard = new UIMRoomEditableCard(container, modeName, gameModeData.icon, () => setActiveTab(0), isViewOnly)
        }

        const mapData = MapDataController.getMapDataForMap(roomState.gameSetting.mapSetting)
        if(mapData != undefined) {
            const mapName = getNestedTranslation({key: 'strings:game.settings.gameInfoTab.map.value'}, {mapName: mapData.name})
            this.mapCard = new UIMRoomEditableCard(container, mapName, mapData.image, () => setActiveTab(1), isViewOnly)
        }

        this.rulesHeader = createHeaderIn(container, {key: 'strings:roomPage.rules.title'}, 'm-room-settings-rules-header')

        this.rulesInformationButton = createLinkIn(this.rulesHeader, '', {key: 'strings:colonistCommon.utils.emptyString'}, 'm-room-settings-rules-information-button-link')
        this.rulesInformationButton.removeAttribute('href')
        createImgIn(this.rulesInformationButton, LobbyImage.InfoIcon, 'm-room-settings-rules-information-button-img')
        this.rulesInformationButton.onclick = () => {
            Sound.playSound(Sound.SoundType.Click)
            new UIMRoomSettingsRulesInformationViewPopup()
        }

        const rulesContainer = createDivIn(container, 'm-room-settings-rules-container')
        this.createToggleButtons(roomState, isViewOnly, rulesContainer)
        this.createSlider(roomState, isViewOnly, container)
    }

    private createSlider(roomState: RoomState, isViewOnly: boolean, container: HTMLDivElement) {
        const sliderSettings: SliderButton[] = [
            {
                id: SliderButtons.MaxPlayers,
                title: {key: 'strings:roomPage.options.maxPlayers.title'},
                value: roomState.gameSetting.maxPlayers,
                min: 1,
                max: RoomSettingConstants.MAX_PLAYER_COUNT,
                step: this.DEFAULT_SLIDER_STEP,
                onChange: (maxPlayers: number) => SocketLobbySend.clickedRoomSettingChangeMaxPlayers(maxPlayers),
            },
            {
                id: SliderButtons.VictoryPoints,
                title: {key: 'strings:roomPage.options.vpToWin.title'},
                value: roomState.gameSetting.victoryPointsToWin,
                min: RoomSettingConstants.MIN_VP_TO_WIN,
                max: RoomSettingConstants.DEFAULT_VP_TO_WIN,
                step: this.DEFAULT_SLIDER_STEP,
                onChange: (victoryPoints: number) => SocketLobbySend.clickedRoomSettingChangeVictoryPointsToWin(victoryPoints),
            },
            {
                id: SliderButtons.CardDiscardLimit,
                title: {key: 'strings:roomPage.options.cardDiscardLimit.title'},
                value: roomState.gameSetting.cardDiscardLimit,
                min: 5,
                max: RoomSettingConstants.MAX_CARD_DISCARD_LIMIT,
                step: this.DEFAULT_SLIDER_STEP,
                onChange: (cardDiscard: number) => SocketLobbySend.clickedRoomSettingChangeCardDiscardLimit(cardDiscard),
            },
            {
                id: SliderButtons.GameSpeed,
                title: {key: 'strings:roomPage.options.gameSpeed.title'},
                value: EnumUtils.getLength(GameSpeedSettings) - roomState.gameSetting.gameSpeed - 1,
                min: 0,
                max: EnumUtils.getLength(GameSpeedSettings) - 1,
                step: this.DEFAULT_SLIDER_STEP,
                onChange: (gameSpeed: number) => {
                    SocketLobbySend.clickedRoomSettingChangeGameSpeed(GameSpeedSettings[EnumUtils.getLength(GameSpeedSettings) - gameSpeed - 1])
                    StorageWrapper.setRoomGameSpeedSetting(EnumUtils.getLength(GameSpeedSettings) - gameSpeed - 1)
                },
                options: ['Very Slow', 'Slow', 'Normal', 'Fast', 'Very Fast'],
            },
            {
                id: SliderButtons.BotSpeed,
                title: {key: 'strings:roomPage.options.botSpeed.title'},
                value: EnumUtils.getLength(BotSpeedSettings) - roomState.gameSetting.botSpeed - 1,
                min: 0,
                max: EnumUtils.getLength(BotSpeedSettings) - 1,
                step: this.DEFAULT_SLIDER_STEP,
                onChange: (botSpeed: number) => {
                    SocketLobbySend.clickedRoomSettingChangeBotSpeed(BotSpeedSettings[EnumUtils.getLength(BotSpeedSettings) - botSpeed - 1])
                    StorageWrapper.setRoomBotSpeedSetting(EnumUtils.getLength(BotSpeedSettings) - botSpeed - 1)
                },
                options: ['Slow', 'Normal', 'Fast'],
            },
        ]
        sliderSettings.forEach((sliderSetting: SliderButton) => {
            const sliderView = new UIRoomSliderController(container,
                sliderSetting.title,
                sliderSetting.value,
                sliderSetting.min,
                sliderSetting.max,
                sliderSetting.step,
                sliderSetting.onChange,
                sliderSetting.options,
                isViewOnly)
            this.sliderButtons.push({button: sliderView, id: sliderSetting.id})
        })
    }

    private createToggleButtons(roomState: RoomState, isViewOnly: boolean, container: HTMLDivElement) {
        const settingsButtons: SettingsButton[] = [
            {
                id: ToggleButtons.PrivateRoom,
                title: {key: 'strings:roomPage.options.privateGame.title'},
                icon: LobbyImage.PrivateGame,
                isActive: roomState.gameSetting.privateGame,
                onClick: (active: boolean) => this.onPrivateGameToggle(active, isViewOnly),
            },
            {
                id: ToggleButtons.HideBankCards,
                title: {key: 'strings:roomPage.options.hideBankCards.title'},
                icon: LobbyImage.HideBankCards,
                isActive: roomState.gameSetting.hideBankCards,
                onClick: (active) => SocketLobbySend.clickedRoomSettingChangeHideBankCards(active),
            },
            {
                id: ToggleButtons.FriendlyRobber,
                title: {key: 'strings:roomPage.options.friendlyRobber.title'},
                icon: LobbyImage.Robber,
                isActive: roomState.gameSetting.friendlyRobber,
                onClick: (active) => {
                    SocketLobbySend.clickedRoomSettingChangeFriendlyRobber(active)
                    StorageWrapper.setRoomFriendlyRobberActiveSetting(active)
                },
            },
            {
                id: ToggleButtons.BalancedDice,
                title: {key: 'strings:roomPage.options.dice.titleMobile'},
                icon: LobbyImage.Dice6,
                isActive: roomState.gameSetting.diceSetting == DiceSettingEnum.Balanced,
                onClick: (active) => {
                    SocketLobbySend.clickedRoomSettingUpdateDiceSetting(active ? DiceSettingEnum[DiceSettingEnum.Balanced] : DiceSettingEnum[DiceSettingEnum.Random])
                    StorageWrapper.setRoomDiceSetting(active ? DiceSettingEnum.Balanced : DiceSettingEnum.Random)
                },
            },
            {
                id: ToggleButtons.NoTroll,
                title: {key: 'strings:roomPage.options.highKarmaGame.title'},
                icon: LobbyImage.IconNoTroll,
                isActive: roomState.isHighKarmaGame(),
                onClick: (active) => {
                    this.handleHighKarmaGameToggle(active)
                    SocketLobbySend.clickedRoomSettingChangeHighKarmaGame(active)
                },
            },
        ]

        settingsButtons.forEach((settingButton: SettingsButton) => {
            const button = new UIMRoomToggableButton(container,
                settingButton.title,
                settingButton.icon,
                settingButton.isActive,
                isViewOnly,
                settingButton.onClick)
            this.toggleButtons.push({button: button, id: settingButton.id})
        })
    }

    private handleHighKarmaGameToggle(active: boolean) {
        const canUserToggleHighKarmaGame = ClientUserState.userState?.items.hasAccessToKarmaFilter()
        if(!canUserToggleHighKarmaGame) {
            notificationController.show({key: 'strings:roomPage.options.highKarmaGame.becomeAMember'}, UITopNotificationType.Information)
            return
        }

        if(this.shouldSyncPrivateRoomStateToServer) {
            this.handleHighKarmaGameToggleSynced(active)
        } else {
            this.handleHighKarmaGameToggleUnSynced(active)
        }
    }

    private handleHighKarmaGameToggleSynced(active: boolean) {
        const isGamePrivate = this.roomState.gameSetting.privateGame
        if(!isGamePrivate) return
        notificationController.show({key: 'strings:roomPage.notifications.highKarmaGameDisabledWhenPrivate'}, UITopNotificationType.Information)
    }

    private handleHighKarmaGameToggleUnSynced(active: boolean) {
        const isGamePrivate = this.unSyncedPrivateRoomState
        if(isGamePrivate) {
            this.updateHighKarmaGameToggle(false)
            notificationController.show({key: 'strings:roomPage.notifications.highKarmaGameDisabledWhenPrivate'}, UITopNotificationType.Information)
            return
        }
        this.updateHighKarmaGameToggle(active)
    }

    private updateHighKarmaGameToggle(active: boolean) {
        this.isHighKarmaGameActive = active
        this.toggleButtons.find((toggleButton) => toggleButton.id == ToggleButtons.NoTroll)?.button.update(active, false)
    }

    private updateToggleButtons(roomState: RoomState, isViewOnly: boolean) {
        this.toggleButtons.forEach((toggleButton) => {
            let updatedState: boolean
            switch(toggleButton.id) {
                case ToggleButtons.PrivateRoom:
                    updatedState = this.shouldSyncPrivateRoomStateToServer ? roomState.gameSetting.privateGame : this.unSyncedPrivateRoomState
                    break
                case ToggleButtons.HideBankCards:
                    updatedState = roomState.gameSetting.hideBankCards
                    break
                case ToggleButtons.FriendlyRobber:
                    updatedState = roomState.gameSetting.friendlyRobber
                    break
                case ToggleButtons.BalancedDice:
                    updatedState = roomState.gameSetting.diceSetting == DiceSettingEnum.Balanced
                    break
                case ToggleButtons.NoTroll:
                    updatedState = this.shouldSyncPrivateRoomStateToServer ? roomState.isHighKarmaGame() : this.isHighKarmaGameActive
                    break
            }
            toggleButton.button.update(updatedState, isViewOnly)
        })
    }

    private updateSliderButtons(roomState: RoomState, isViewOnly: boolean) {
        this.sliderButtons.forEach((sliderButton) => {
            let updatedValue: number
            switch(sliderButton.id) {
                case SliderButtons.MaxPlayers:
                    updatedValue = roomState.gameSetting.maxPlayers
                    break
                case SliderButtons.VictoryPoints:
                    sliderButton.button.updateSliderTicks(roomState.victoryPointsMaxAllowed)
                    sliderButton.button.setWarningThreshold(roomState.victoryPointsRecommendedLimit)
                    updatedValue = roomState.gameSetting.victoryPointsToWin
                    break
                case SliderButtons.CardDiscardLimit:
                    updatedValue = roomState.gameSetting.cardDiscardLimit
                    break
                case SliderButtons.GameSpeed:
                    updatedValue = EnumUtils.getLength(GameSpeedSettings) - roomState.gameSetting.gameSpeed - 1
                    break
                case SliderButtons.BotSpeed:
                    updatedValue = EnumUtils.getLength(BotSpeedSettings) - roomState.gameSetting.botSpeed - 1
                    break
            }
            sliderButton.button.setValue(updatedValue, isViewOnly)
        })
    }

    update(roomState: RoomState) {
        this.roomState = roomState
        const isViewOnly = !roomState.isHost(socket.controller.session.id)
        const gameModeData = GameModeDataController.getModeDataForMode(roomState.gameSetting.modeSetting)
        const mapData = MapDataController.getMapDataForMap(roomState.gameSetting.mapSetting)
        if(gameModeData == undefined || mapData == undefined) return

        const modeName = `Game mode: ${clientI18n.t(gameModeData.name.key, gameModeData.name.options)}`
        this.gamemodeCard.update(modeName, gameModeData.icon, isViewOnly)

        const mapName = `Map: ${clientI18n.t(mapData.name.key, mapData.name.options)}`
        this.mapCard.update(mapName, mapData.image, isViewOnly)

        this.updateToggleButtons(roomState, isViewOnly)
        this.updateSliderButtons(roomState, isViewOnly)
    }

    onPrivateGameToggle(active: boolean, isViewOnly: boolean): void {
        if(this.shouldSyncPrivateRoomStateToServer) return SocketLobbySend.clickedRoomSettingChangePrivateGame(active)
        Sound.playSound(Sound.SoundType.RoomSettingUpdated)

        const privateRoomButton = this.toggleButtons.find((toggleButton) => toggleButton.id == ToggleButtons.PrivateRoom)
        privateRoomButton?.button.update(active, isViewOnly)
        this.unSyncedPrivateRoomState = active
        if(active && this.isHighKarmaGameActive) this.handleHighKarmaGameToggle(this.isHighKarmaGameActive)
    }

    setPrivateRoomStateSync(isActive: boolean): void {
        this.shouldSyncPrivateRoomStateToServer = isActive
        if(!isActive) return
        SocketLobbySend.clickedRoomSettingChangePrivateGame(this.unSyncedPrivateRoomState)
        SocketLobbySend.clickedRoomSettingChangeHighKarmaGame(this.isHighKarmaGameActive)
    }
}
